import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Lock, ArrowLeft, LogIn, Maximize } from "lucide-react";
import { useFilms } from "../context/FilmsContext";
import { InnerNav } from "../components/landing/InnerNav";
import { useAuth } from "../context/AuthContext";
import { startCheckout } from "../lib/checkout";
import { recordProgress, fetchFilmPosition } from "../lib/progress";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Watch() {
  const { id } = useParams();
  const { films, loading: filmsLoading } = useFilms();
  const film = films.find((f) => f.id === id);
  const { user, login } = useAuth();
  const [busy, setBusy] = useState(false);
  const [embedUrl, setEmbedUrl] = useState(null);
  const playerRef = useRef(null);
  const iframeRef = useRef(null);
  const positionRef = useRef(0);
  const gotPMRef = useRef(false);
  const lastSavedRef = useRef(-1);

  const subscribePlayer = () => {
    const win = iframeRef.current && iframeRef.current.contentWindow;
    if (!win) return;
    ["timeupdate", "play", "pause"].forEach((ev) =>
      win.postMessage(
        JSON.stringify({ context: "player.js", version: "0.0.1", method: "addEventListener", value: ev }),
        "*"
      )
    );
  };

  const goFullscreen = () => {
    const el = playerRef.current;
    if (!el) return;
    const req =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.webkitEnterFullscreen ||
      el.msRequestFullscreen;
    if (req) req.call(el);
  };

  useEffect(() => {
    if (!(film && user && user.subscribed)) return;
    let active = true;
    let saveTimer;
    gotPMRef.current = false;
    positionRef.current = 0;
    lastSavedRef.current = -1;
    setEmbedUrl(null);

    const save = () => {
      const p = positionRef.current;
      if (p > 0 && Math.abs(p - lastSavedRef.current) >= 1) {
        lastSavedRef.current = p;
        recordProgress(film.id, p);
      }
    };

    const setup = async () => {
      const [start, stream] = await Promise.all([
        fetchFilmPosition(film.id),
        axios
          .get(`${API}/films/${film.id}/stream`, { withCredentials: true })
          .then((r) => r.data)
          .catch(() => null),
      ]);
      if (!active) return;
      positionRef.current = start || 0;
      if (!stream || !stream.embed_url) {
        setEmbedUrl(false);
        return;
      }
      let src = stream.embed_url;
      if (start && start > 5) src += (src.includes("?") ? "&" : "?") + "start=" + Math.floor(start);
      setEmbedUrl(src);
      recordProgress(film.id, positionRef.current);
      lastSavedRef.current = positionRef.current;
      saveTimer = setInterval(save, 10000);
    };
    setup();

    const onMessage = (e) => {
      let data = e.data;
      try {
        if (typeof data === "string") data = JSON.parse(data);
      } catch {
        return;
      }
      if (!data || data.context !== "player.js") return;
      if (data.event === "ready") {
        subscribePlayer();
      } else if (data.event === "timeupdate" && data.value && typeof data.value.seconds === "number") {
        gotPMRef.current = true;
        positionRef.current = data.value.seconds;
      }
    };
    window.addEventListener("message", onMessage);

    const onVis = () => {
      if (document.visibilityState === "hidden") save();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", save);

    return () => {
      active = false;
      clearInterval(saveTimer);
      window.removeEventListener("message", onMessage);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", save);
      save();
    };
  }, [film?.id, user?.user_id, user?.subscribed]);

  const subscribe = async () => {
    if (!user) {
      login();
      return;
    }
    setBusy(true);
    try {
      startCheckout(user.email, user.user_id);
    } catch {
      toast.error("Could not start checkout.");
      setBusy(false);
    }
  };

  if (!film) {
    if (filmsLoading) {
      return (
        <div className="min-h-screen bg-background text-foreground">
          <InnerNav />
          <div className="max-w-2xl mx-auto px-5 py-32 flex justify-center">
            <Loader2 className="h-7 w-7 text-blood animate-spin" />
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-background text-foreground">
        <InnerNav />
        <div className="max-w-2xl mx-auto px-5 py-32 text-center">
          <h1 className="font-serif text-4xl text-bone tracking-tighter">
            Title not found
          </h1>
          <Link to="/archive" className="mt-8 inline-block font-mono text-[11px] tracking-[0.25em] uppercase text-blood">
            Back to the archive
          </Link>
        </div>
      </div>
    );
  }

  const loading = user === null;
  const subscribed = user && user.subscribed;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="watch-page">
      <InnerNav active="archive" />

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-8 md:pt-12 pb-24">
        <Link
          to="/archive"
          data-testid="watch-back"
          className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground hover:text-bone transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Archive
        </Link>

        <div className="mt-6 flex items-baseline gap-4 flex-wrap">
          <h1 className="font-serif text-4xl md:text-6xl leading-[0.9] tracking-tighter text-bone">
            {film.title}
          </h1>
          <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-blood">
            {film.category} · {film.year} · {film.runtime}
          </span>
        </div>
        <p className="mt-3 font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
          Directed by {film.director}
        </p>

        {loading ? (
          <div className="mt-8 relative w-full aspect-video border border-white/12 overflow-hidden bg-black flex items-center justify-center">
            <Loader2 className="h-7 w-7 text-blood animate-spin" />
          </div>
        ) : subscribed ? (
          <div className="mt-8">
            <div
              ref={playerRef}
              className="relative w-full aspect-video border border-white/12 overflow-hidden bg-black"
            >
              {embedUrl ? (
                <iframe
                  data-testid="film-player"
                  ref={iframeRef}
                  title={film.title}
                  src={embedUrl}
                  onLoad={subscribePlayer}
                  className="absolute inset-0 h-full w-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                  allowFullScreen
                  webkitallowfullscreen="true"
                  mozallowfullscreen="true"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  {embedUrl === false ? (
                    <p data-testid="player-error" className="font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
                      Couldn't load this title. Refresh to try again.
                    </p>
                  ) : (
                    <Loader2 className="h-7 w-7 text-blood animate-spin" />
                  )}
                </div>
              )}
            </div>
            <button
              data-testid="fullscreen-btn"
              onClick={goFullscreen}
              className="mt-3 inline-flex items-center gap-2 border border-white/20 hover:border-blood hover:bg-blood px-5 py-2.5 font-mono text-[11px] tracking-[0.25em] uppercase transition-all duration-300"
            >
              <Maximize className="h-4 w-4" /> Fullscreen
            </button>
          </div>
        ) : (
          <div data-testid="paywall" className="mt-8 relative w-full min-h-[560px] md:min-h-[500px] border border-white/12 overflow-hidden bg-black flex items-center justify-center py-14 px-4">
            <img src={film.poster} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 grayscale blur-sm scale-105" />
              <div className="absolute inset-0 bg-black/75" />
              <div className="relative text-center px-6 max-w-lg">
                <span className="h-12 w-12 mx-auto rounded-full bg-blood/20 border border-blood/50 flex items-center justify-center">
                  <Lock className="h-5 w-5 text-blood" />
                </span>
                <h2 className="mt-6 font-serif text-3xl md:text-4xl tracking-tighter text-bone">
                  {user ? "Subscribe to watch" : "Sign in to watch"}
                </h2>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  {user
                    ? `All ${films.length} restored classics for two dollars a month. Cancel anytime.`
                    : "Sign in with Google, then unlock the full archive for two dollars a month."}
                </p>
                <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                  {!user && (
                    <button
                      data-testid="paywall-signin"
                      onClick={login}
                      className="inline-flex items-center justify-center gap-2 bg-bone text-black px-7 py-3.5 font-mono text-[11px] tracking-[0.25em] uppercase transition-transform hover:-translate-y-0.5"
                    >
                      <LogIn className="h-4 w-4" /> Sign in with Google
                    </button>
                  )}
                  <button
                    data-testid="paywall-monthly"
                    onClick={() => subscribe()}
                    disabled={busy}
                    className="bg-blood text-white px-7 py-3.5 font-mono text-[11px] tracking-[0.25em] uppercase transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                  >
                    {busy ? "Opening…" : "$2 / month"}
                  </button>
                </div>
              </div>
            </div>
          )}

        <p className="mt-6 max-w-2xl text-base text-muted-foreground leading-relaxed">
          {film.blurb}
        </p>
      </div>
    </div>
  );
}
